import type { Edge } from "@xyflow/react";
import * as React from "react";
import type {
  FlowDefinition,
  NodeDefinition,
  ThinkerOption,
} from "./types";

export type { ThinkerOption };

interface NodePanelProps {
  selectedNode: NodeDefinition | null;
  selectedEdge: Edge | null;
  onNodePatch: (nodeId: string, patch: Partial<NodeDefinition>) => void;
  onEdgeLabelChange: (edgeId: string, label: string) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  flow: FlowDefinition | null;
  onFlowPatch: (patch: Partial<FlowDefinition>) => void;
  thinkers: ThinkerOption[];
}

// Fallback list when the server's thinker registry is empty/unreachable, so the
// dropdown isn't blank on a fresh DB.
const FALLBACK_THINKERS: ThinkerOption[] = [
  { id: "ide:claude", name: "ide:claude", displayName: "Claude (IDE)" },
  { id: "Human", name: "Human", displayName: "Human" },
  { id: "DeveloperAI", name: "DeveloperAI", displayName: "Developer AI" },
];

/**
 * Right-rail editor. Three views, picked by what's selected:
 *   1. Node selected   → name, ownerRole (from real thinker list), tool, approval, instructions, delete
 *   2. Edge selected   → condition (free text, DSL expression)
 *   3. Nothing         → flow-level fields
 */
export function NodePanel(props: NodePanelProps) {
  const {
    selectedNode,
    selectedEdge,
    onNodePatch,
    onEdgeLabelChange,
    onDeleteNode,
    onDeleteEdge,
    flow,
    onFlowPatch,
    thinkers,
  } = props;

  const thinkerChoices = React.useMemo<ThinkerOption[]>(() => {
    if (thinkers && thinkers.length > 0) {
      return thinkers;
    }
    return FALLBACK_THINKERS;
  }, [thinkers]);

  if (selectedEdge) {
    const condition =
      typeof selectedEdge.label === "string" ? selectedEdge.label : "";
    return (
      <aside className="fn-panel">
        <h3 className="fn-panel-title">Transition</h3>
        <div className="fn-panel-row">
          <label>From → To</label>
          <div className="fn-panel-readonly">
            {selectedEdge.source} → {selectedEdge.target}
          </div>
        </div>
        <div className="fn-panel-row">
          <label>Condition</label>
          <input
            type="text"
            value={condition}
            onChange={(e) => onEdgeLabelChange(selectedEdge.id, e.target.value)}
            placeholder='e.g. always  |  contains(prev.result, "VERDICT: ITERATE")'
          />
          <div className="fn-panel-hint">
            DSL: <code>always</code>,{" "}
            <code>contains(prev.result, &quot;…&quot;)</code>,{" "}
            <code>vars.score &gt;= 0.8</code>. First matching transition wins.
          </div>
        </div>
        <div className="fn-panel-row fn-panel-danger-row">
          <button
            type="button"
            className="fn-btn fn-btn-danger"
            onClick={() => {
              if (
                window.confirm(
                  `Delete this transition (${selectedEdge.source} → ${selectedEdge.target})?`
                )
              ) {
                onDeleteEdge(selectedEdge.id);
              }
            }}
          >
            🗑 Delete transition
          </button>
          <div className="fn-panel-hint">
            Removes this edge only. Or press Delete / Backspace with the edge
            selected.
          </div>
        </div>
      </aside>
    );
  }

  if (selectedNode) {
    const currentRole = selectedNode.ownerRole || "";
    // If the persisted ownerRole isn't in the fetched list (e.g. legacy
    // strings that pre-date the typed registry), surface it anyway so the
    // user can see what's set without losing the value.
    const showCustomRow =
      currentRole &&
      !thinkerChoices.some(
        (t) => t.name === currentRole || t.id === currentRole
      );

    return (
      <aside className="fn-panel">
        <h3 className="fn-panel-title">Node</h3>
        <div className="fn-panel-row">
          <label>Node id</label>
          <div className="fn-panel-readonly">{selectedNode.nodeId}</div>
        </div>
        <div className="fn-panel-row">
          <label>Name</label>
          <input
            type="text"
            value={selectedNode.name || ""}
            onChange={(e) =>
              onNodePatch(selectedNode.nodeId, { name: e.target.value })
            }
          />
        </div>
        <div className="fn-panel-row">
          <label>Owner role (thinker)</label>
          <select
            value={currentRole}
            onChange={(e) =>
              onNodePatch(selectedNode.nodeId, { ownerRole: e.target.value })
            }
          >
            {!currentRole && <option value="">— select a thinker —</option>}
            {showCustomRow && (
              <option value={currentRole}>
                {currentRole} (current — not in registry)
              </option>
            )}
            {thinkerChoices.map((t) => (
              <option key={t.id} value={t.name || t.id}>
                {t.displayName || t.name || t.id}
                {t.isAI === true ? " (AI)" : t.isAI === false ? " (Human)" : ""}
              </option>
            ))}
          </select>
          <div className="fn-panel-hint">
            {thinkers.length === 0
              ? "Live thinker list unavailable — showing built-in defaults."
              : `${thinkers.length} thinkers available from your server.`}
          </div>
        </div>
        <div className="fn-panel-row">
          <label>Tool</label>
          <input
            type="text"
            value={selectedNode.toolName || "NoOpTool"}
            onChange={(e) =>
              onNodePatch(selectedNode.nodeId, { toolName: e.target.value })
            }
          />
        </div>
        <div className="fn-panel-row fn-panel-checkbox">
          <label>
            <input
              type="checkbox"
              checked={!!selectedNode.requireApproval}
              onChange={(e) =>
                onNodePatch(selectedNode.nodeId, {
                  requireApproval: e.target.checked,
                })
              }
            />
            Require human approval before running
          </label>
        </div>
        <div className="fn-panel-row fn-panel-checkbox">
          <label>
            <input
              type="checkbox"
              checked={!!selectedNode.queueTransport?.enabled}
              onChange={(e) => {
                const current = selectedNode.queueTransport;
                onNodePatch(selectedNode.nodeId, {
                  queueTransport: e.target.checked
                    ? { ...(current ?? {}), enabled: true }
                    : current?.queue
                    ? { ...current, enabled: false }
                    : undefined,
                });
              }}
            />
            Queue transport (RabbitMQ)
          </label>
          <div className="fn-panel-hint">
            Pass this node&apos;s result through the message queue as well as
            the run state — consumers and the queue admin can then see it.
          </div>
        </div>
        {selectedNode.queueTransport?.enabled && (
          <div className="fn-panel-row">
            <label>Queue name (optional override)</label>
            <input
              type="text"
              value={selectedNode.queueTransport?.queue || ""}
              placeholder="knowit.flow.{slug}.{nodeId}.out.v1"
              onChange={(e) =>
                onNodePatch(selectedNode.nodeId, {
                  queueTransport: {
                    ...(selectedNode.queueTransport ?? { enabled: true }),
                    enabled: true,
                    queue: e.target.value || undefined,
                  },
                })
              }
            />
            <div className="fn-panel-hint">
              Leave blank for the default:{" "}
              <code>knowit.flow.{"{slug}"}.{"{nodeId}"}.out.v1</code>
            </div>
          </div>
        )}
        <div className="fn-panel-row">
          <label>Transaction group (optional)</label>
          <input
            type="text"
            value={selectedNode.txn?.group || ""}
            placeholder="e.g. ingest-batch"
            onChange={(e) => {
              const group = e.target.value.trim();
              onNodePatch(selectedNode.nodeId, {
                txn: group
                  ? { ...(selectedNode.txn ?? {}), group }
                  : undefined,
              });
            }}
          />
          <div className="fn-panel-hint">
            Nodes sharing a group are one transaction: their queue effects are
            held until the commit node completes, and a run broken mid-group
            aborts them — nothing half-true leaves the run.
          </div>
        </div>
        {selectedNode.txn?.group && (
          <div className="fn-panel-row fn-panel-checkbox">
            <label>
              <input
                type="checkbox"
                checked={!!selectedNode.txn?.commit}
                onChange={(e) =>
                  onNodePatch(selectedNode.nodeId, {
                    txn: {
                      ...(selectedNode.txn ?? { group: "" }),
                      commit: e.target.checked || undefined,
                    },
                  })
                }
              />
              Commit node — completing it flushes the group&apos;s held effects
            </label>
          </div>
        )}
        <div className="fn-panel-row">
          <label>Instructions</label>
          <textarea
            rows={14}
            value={selectedNode.instructions || ""}
            onChange={(e) =>
              onNodePatch(selectedNode.nodeId, { instructions: e.target.value })
            }
            placeholder={
              "You are X, a [role]. Your specialty is […].\n\n" +
              "## Previous step\n{{prev.result}}\n\n## Task\n…\n\n" +
              'End with "SLUG: <slug> | <summary>".'
            }
          />
        </div>
        <div className="fn-panel-row fn-panel-danger-row">
          <button
            type="button"
            className="fn-btn fn-btn-danger"
            onClick={() => {
              if (
                window.confirm(
                  `Delete node "${
                    selectedNode.name || selectedNode.nodeId
                  }" and all its transitions?`
                )
              ) {
                onDeleteNode(selectedNode.nodeId);
              }
            }}
          >
            🗑 Delete node
          </button>
          <div className="fn-panel-hint">
            Removes the node and every edge that touches it. Or press Delete /
            Backspace with the node selected.
          </div>
        </div>
      </aside>
    );
  }

  // Nothing selected → flow-level fields.
  return (
    <aside className="fn-panel">
      <h3 className="fn-panel-title">Flow</h3>
      <div className="fn-panel-row">
        <label>Id</label>
        <div className="fn-panel-readonly">
          {flow?.id || "(new — will be assigned on save)"}
        </div>
      </div>
      <div className="fn-panel-row">
        <label>Name</label>
        <input
          type="text"
          value={flow?.name || ""}
          onChange={(e) => onFlowPatch({ name: e.target.value })}
        />
      </div>
      <div className="fn-panel-row">
        <label>Description</label>
        <textarea
          rows={4}
          value={flow?.description || ""}
          onChange={(e) => onFlowPatch({ description: e.target.value })}
        />
      </div>
      <div className="fn-panel-row">
        <label>Start node</label>
        <select
          value={flow?.startNodeId || ""}
          onChange={(e) => onFlowPatch({ startNodeId: e.target.value })}
        >
          {(flow?.nodes || []).map((n) => (
            <option key={n.nodeId} value={n.nodeId}>
              {n.nodeId} — {n.name}
            </option>
          ))}
        </select>
      </div>
      <div className="fn-panel-row">
        <label>Max nodes</label>
        <input
          type="number"
          min={1}
          step={1}
          value={
            typeof flow?.maxNodes === "number" ? String(flow.maxNodes) : ""
          }
          placeholder="default (tomato.flow.maxNodes)"
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (raw === "") {
              onFlowPatch({ maxNodes: undefined });
              return;
            }
            const n = Math.floor(Number(raw));
            onFlowPatch({
              maxNodes: Number.isFinite(n) && n > 0 ? n : undefined,
            });
          }}
        />
        <div className="fn-panel-hint">
          Cycle-breaker: max node executions before a run aborts. Leave blank to
          use the global <code>tomato.flow.maxNodes</code> setting.
        </div>
      </div>
      <div className="fn-panel-hint">
        Click a node to edit its persona/instructions. Click an edge to edit its
        transition condition. Drag from a node&apos;s bottom handle to
        another&apos;s top handle to create a new transition. Press Delete with
        a node or edge selected to remove it.
      </div>
    </aside>
  );
}
